import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { LoadingService } from "src/app/ui/services/loading.service";
import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { BeneficiaryService } from "src/app/ui/services/pbm/beneficiary.service";
import { AppContext } from "src/app/ui/contexts/app-context";
import { PagingRequest } from "src/app/ui/models/domains/paginator/paging-request";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { Beneficiary } from "src/app/ui/models/domains/pbm/Beneficiary/beneficiary";
import { PageableResult } from "src/app/ui/models/domains/paginator/pageable-result";
import { Toast } from "../../../toast/toast";
import { UserPermissions } from "./../../../../models/domains/pbm/user/user-permissions";
import { UserService } from "src/app/ui/services/user.service";
import { ICompanyFilterParams } from "src/app/ui/models/domains/pbm/companies/company-filter-params";
import { LimitBeneficiaryModalComponent } from "./limit-beneficiary-modal/limit-beneficiary-modal.component";

@Component({
  selector: "search-beneficiary",
  templateUrl: "./search-beneficiary.component.html",
  styleUrls: ["./search-beneficiary.component.scss"],
})
export class SearchBeneficiaryComponent implements OnInit {
  public searchCompanyFirst = true;
  public pagingRequest = new PagingRequest();
  public pageableResult = new PageableResult();
  public companyPageableResult = new PageableResult();
  public filterText = "";
  public filter = "";
  public fiscalNumberMask = MaskInput.getFicalNumberMask();
  public searchType = new FormControl(1);
  public searchStatus = new FormControl(3);
  public statusPreference = 3;

  private company: Company | null = null;
  private userProfile = this.userService.getUserProfile();

  constructor(
    private featureViewService: FeatureViewService,
    private loadingService: LoadingService,
    private companyService: CompanyService,
    private beneficiaryService: BeneficiaryService,
    private appContext: AppContext,
    private toast: Toast,
    private router: Router,
    private userService: UserService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.userProfile = this.userService.getUserProfile();
    const savedFilter = this.userProfile.getPreferences().getFilterText() || "";
    this.searchCompanies(savedFilter);
    this.featureViewService.activeFeatureView(
      FeatureViews.BeneficiarySearchFeatureView,
      PageViews.BeneficiaryPageView
    );
  }

  // --- Company Search Methods ---
  onPressEnterSearchCompanies(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.statusPreference = this.searchStatus.value;
      this.searchCompanies(this.filterText);
    }
  }

  searchCompanies(filterText: string): void {
    this.statusPreference = this.searchStatus.value;
    this.searchCompanyFirst = true;
    this.doSearchCompany(1, this.appContext.getTotalPerPage(), filterText);
  }

  searchMoreCompanies(): void {
    this.searchCompanyFirst = false;
    const start = this.companyPageableResult.getEnd() + 1;
    this.doSearchCompany(start, this.appContext.getTotalPerPage(), this.userProfile.getPreferences().getFilterText());
  }

  private doSearchCompany(start: number, end: number, filterText: string): void {
    const ref = this.loadingService.beginLoading();
    const pagingRequest = new PagingRequest();
    pagingRequest.setStart(start);
    pagingRequest.setEnd(end);

    const filterParams: ICompanyFilterParams = {
      filterText: filterText ? filterText.replace(/[./-]/g, "") : "",
      status: this.statusPreference,
    };

    this.companyService
      .searchCompany(filterParams, pagingRequest)
      .then((result) => {
        this.companyPageableResult.setStart(start);
        this.companyPageableResult.setEnd(end);
        this.companyPageableResult.setTotalItems(result.getTotalItems());

        if (this.searchCompanyFirst) {
          this.companyPageableResult.setItems(result.getItems());
        } else {
          this.companyPageableResult.addItems(result.getItems());
        }

        if (result.getItems().length === 1) {
          this.company = result.getItems()[0] as Company;
          this.navigateToCompanyBeneficiaries(this.company);
          this.storageSearchPreferences(filterText);
        } else {
          this.company = null;
          this.storageSearchPreferences(null);
        }
      })
      .catch(() => {
        this.companyPageableResult.setItems(null);
      })
      .finally(() => {
        this.loadingService.finishLoading(ref);
        this.filterText = "";
      });
  }

  // --- Beneficiary Search Methods ---
  onPressEnterSearchBeneficiary(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.searchBeneficiary();
    }
  }

  async searchBeneficiary(): Promise<void> {
    const ref = this.loadingService.beginLoading();
    try {
      this.pagingRequest = new PagingRequest();
      this.pagingRequest.setStart(1);
      this.pagingRequest.setEnd(this.appContext.getTotalPerPage());
      this.pagingRequest.setFilterText(this.filter.replace(/[.-]/g, ""));
      this.pageableResult = await this.beneficiaryService.findBeneficiariesByFiscalNumber(
        this.userProfile.getPreferences().getFilterText(),
        this.pagingRequest,
        false,
      );
    } catch (error) {
      this.toast.showWarningToast("Beneficiário não encontrado");
      console.log(error);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  async searchMoreBeneficiaries(): Promise<void> {
    const ref = this.loadingService.beginLoading();
    try {
      const start = this.pagingRequest.getStart() + this.appContext.getTotalPerPage();
      this.pagingRequest = new PagingRequest();
      this.pagingRequest.setStart(start);
      this.pagingRequest.setEnd(this.appContext.getTotalPerPage());
      this.pagingRequest.setFilterText(this.filter);
      const moreResults = await this.beneficiaryService.findBeneficiariesByFiscalNumber(
        this.userProfile.getPreferences().getFilterText(),
        this.pagingRequest,
        false,
      );
      this.pageableResult.addItems(moreResults.getItems());
    } catch {
      this.toast.showWarningToast("Beneficiário não encontrado");
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  // --- Navigation & Modal ---
  async navigateToCompanyBeneficiaries(company: Company): Promise<void> {
    const ref = this.loadingService.beginLoading();
    try {
      this.company = await this.companyService.findCompanyBy(company.getFiscalNumber());
      this.pagingRequest = new PagingRequest();
      this.pagingRequest.setStart(1);
      this.pagingRequest.setEnd(this.appContext.getTotalPerPage());
      this.pagingRequest.setFilterText("");
      this.pageableResult = await this.beneficiaryService.findBeneficiariesByFiscalNumber(
        company.getFiscalNumber(),
        this.pagingRequest,
        false,
      );
      this.userService.setUserPreferences(company.getFiscalNumber());
    } catch {
      this.company = null;
      this.toast.showWarningToast("Empresa não encontrada");
    } finally {
      this.loadingService.finishLoading(ref);
      this.storageSearchPreferences(company.getFiscalNumber());
      this.filterText = "";
    }
  }

  navigateToEditBeneficiary(beneficiary: Beneficiary): void {
    const beneficiaryKey = beneficiary.getBeneficiaryKey().getValue();
    this.router.navigate([RoutePath.EditBeneficiary, beneficiaryKey]);
  }

  public openModalBeneficiaryLimit(beneficiary: Beneficiary): void {
    const dialogRef = this.dialog.open(LimitBeneficiaryModalComponent, {
      id: "status-dialog",
      disableClose: true,
      hasBackdrop: true,
      width: "860px",
      height: "450px",
      data: { beneficiary },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigateToCompanyBeneficiaries(this.company);
      }
    });
  }

  // --- Permissions ---
  hasAccessToEditBeneficiary(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditBeneficiary);
  }

  hasAccessToEditCycleLimit(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditCycleLimit);
  }

  // --- Getters & Utilities ---
  setCompany(company: Company): void {
    this.company = company;
  }

  hasCompany(): boolean {
    return !!this.company;
  }

  getCompanies(): Company[] {
    return this.companyPageableResult.getItems() as Company[] || [];
  }

  getCompany(): Company | null {
    return this.company;
  }

  getPagebleResultItems(): Beneficiary[] {
    return this.pageableResult?.getItems() as Beneficiary[] || [];
  }

  getPeagebleResultItemsLenght(): number {
    return this.pageableResult?.getItems().length || 0;
  }

  storageSearchPreferences(fiscalNumber: string | null): void {
    this.userService.setUserPreferences(fiscalNumber ? fiscalNumber.replace(/[./-]/g, "") : "");
    this.userProfile = this.userService.getUserProfile();
  }

  showScrollBottomBtn(): boolean {
    return this.companyPageableResult.getItems().length > 1;
  }

  showMoreCompaniesBtn(): boolean {
    return (
      this.companyPageableResult.getTotalItems() !== this.companyPageableResult.getItems().length &&
      this.companyPageableResult.getItems().length > 1
    );
  }

  showMoreBeneficiaries(): boolean {
    return (
      this.pageableResult &&
      this.pageableResult.getTotalItems() !== this.pageableResult.getItems().length &&
      this.pageableResult.getItems().length > 1
    );
  }

  scrollToLastCompany(): void {
    const bottom = document.getElementById("bottom");
    bottom?.scrollIntoView();
  }

  getResultQuantityPhrase(): string {
    const length = this.pageableResult?.getItems().length || 0;
    return length === 0 ? "Nenhum resultado encontrado." : `${length} RESULTADO(S) ENCONTRADO(S)`;
  }

  getCompanyResultQuantityPhrase(): string {
    const length = this.companyPageableResult?.getItems().length || 0;
    return length === 0 ? "Nenhum resultado encontrado." : `${length} RESULTADO(S) ENCONTRADO(S)`;
  }
}